/**
 * ASW-2 — student lifecycle presentation helpers.
 * Continuity vocabulary + live status hierarchy. Consumes existing derive/chrome —
 * does not invent FLOW_STATE / L1 transitions.
 */

import { EXT_CONNECTIVITY } from './assessmentExperienceDerive';

/** Shared CTA / caption vocabulary across intro → live → complete → results. */
export const LIFECYCLE_CTA = Object.freeze({
  START: 'Start assessment',
  CONTINUE: 'Continue assessment',
  VIEW_SUMMARY: 'View summary',
  VIEW_RESULTS: 'View results',
  WAITING_WINDOW: 'Waiting for window',
  FINISH: 'Finish assessment',
  BACK_TO_SUMMARY: 'Back to summary',
  RETURN_TO_LIST: 'Return to assessments',
});

export function resultsUnlockCaption() {
  return 'Results pending faculty release.';
}

/**
 * Body connection card — suppress when chrome strip + stale notice already tell the story.
 * Keep "Connection restored" and optional guided heartbeat detail.
 */
export function shouldRenderLiveConnectionCard({
  pollOk = true,
  previouslyInterrupted = false,
  extensionConnectivity = null,
  lastHeartbeatAt = null,
} = {}) {
  if (!pollOk) return false;
  if (previouslyInterrupted) return true;

  if (
    extensionConnectivity === EXT_CONNECTIVITY.EXT_DEGRADED ||
    extensionConnectivity === EXT_CONNECTIVITY.EXT_UNKNOWN
  ) {
    return false;
  }

  if (extensionConnectivity === EXT_CONNECTIVITY.EXT_GUIDED) {
    return Boolean(lastHeartbeatAt);
  }

  // Legacy path without EXT_* axis — keep prior connectionPresentation behaviour.
  return true;
}

/** State banner — skip phases owned by chrome strip / stale notice. */
export function shouldRenderLiveStateBanner(phase) {
  if (!phase || phase === 'active' || phase === 'connection_loss') return false;
  return true;
}

const CLOSED_ASSESSMENT_STATUSES = new Set(['closed', 'reconciling', 'cancelled']);

/** Faculty ended the assessment window (manual close or cancelled). */
export function isAssessmentClosedForStudents(assessmentStatus) {
  return CLOSED_ASSESSMENT_STATUSES.has(assessmentStatus);
}

/**
 * True when the student should still see an in-progress affordance.
 * Closed assessments must not linger as "in progress" on dashboard/resume chrome.
 */
export function isStudentAttemptInProgress(item, liveView) {
  const assessmentStatus = liveView?.assessment?.status ?? item?.status ?? null;
  if (isAssessmentClosedForStudents(assessmentStatus)) return false;
  const attemptState = liveView?.state ?? item?.attemptState ?? null;
  return attemptState === 'active';
}

/**
 * Faculty monitor row label — expired after manual close reads as Closed, not expired.
 */
export function formatMonitorAttemptState(attemptState, assessmentStatus) {
  if (isAssessmentClosedForStudents(assessmentStatus) && attemptState === 'expired') {
    return 'Closed';
  }
  if (!attemptState) return '—';
  return attemptState;
}

/**
 * Completion / live outcome copy keyed by attempt + assessment status.
 * Presentation only — does not change platform state.
 */
export function studentAttemptOutcomePresentation({ attemptState, assessmentStatus } = {}) {
  if (isAssessmentClosedForStudents(assessmentStatus) && attemptState === 'expired') {
    return {
      variant: 'closed',
      statusLabel: 'Assessment closed',
      overline: 'Assessment closed',
      headline: 'Assessment closed',
      detail:
        'Your faculty ended this assessment. Work you completed before the close has been recorded.',
      showSuccessAlert: false,
    };
  }
  if (attemptState === 'submitted') {
    return {
      variant: 'submitted',
      statusLabel: 'Submitted',
      overline: 'Assessment complete',
      headline: null,
      detail: 'Your submission was received successfully.',
      showSuccessAlert: true,
    };
  }
  if (attemptState === 'expired') {
    return {
      variant: 'expired',
      statusLabel: 'Time expired',
      overline: 'Time expired',
      headline: null,
      detail: 'Your attempt window ended and your work has been recorded.',
      showSuccessAlert: true,
    };
  }
  return {
    variant: 'submitted',
    statusLabel: attemptState || 'Submitted',
    overline: 'Assessment complete',
    headline: null,
    detail: 'Your submission was received successfully.',
    showSuccessAlert: true,
  };
}
